"use client";

import Script from "next/script";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { GA_ENABLED, GA_MEASUREMENT_ID, sendPageView } from "@/lib/consent";
import { useConsent } from "./ConsentContext";

/**
 * Loads gtag.js only once analytics consent is granted, and sends exactly one
 * page_view per route.
 *
 * usePathname() and not useSearchParams(): the latter would opt the whole tree
 * out of static rendering, and we don't want query strings in GA anyway (see
 * sendPageView).
 *
 * Requires "Page changes based on browser history events" to be OFF in the GA4
 * data stream's Enhanced measurement settings — otherwise every #anchor click
 * in the home nav registers a second, bogus page view.
 */
export function GoogleAnalytics() {
  const { status } = useConsent();
  const pathname = usePathname();

  useEffect(() => {
    if (status !== "granted" || !pathname) return;
    // One frame of slack so App Router has swapped <title> for the new route
    // before we read it — otherwise a client-side nav reports the old title.
    const frame = requestAnimationFrame(() => sendPageView(pathname));
    return () => cancelAnimationFrame(frame);
  }, [status, pathname]);

  if (!GA_ENABLED || status !== "granted") return null;

  return (
    <Script
      id="wz-ga"
      strategy="afterInteractive"
      src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
    />
  );
}
